import { graphql } from 'react-relay';
import { useCallback, useState, useEffect, useRef } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import type {
	useContentTypesTitleUpdateContentTitleMutation as useContentTypesTitleUpdateContentTitleMutation$data,
	useContentTypesTitleUpdateContentTitleMutationVariables as useContentTypesTitleUpdateContentTitleMutation$variables,
} from '@confluence/content-types-utils/entry-points/useContentTypesTitleUpdateContentTitleMutation.ts';
import { contentTypesTitleServiceSingleton } from '@confluence/content-types-header-base';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { fg } from '@confluence/feature-gating';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type { useContentTypesTitleUpdateContentTitleWriteFragment$data } from './__generated__/useContentTypesTitleUpdateContentTitleWriteFragment.graphql.ts';
import useContentTypesTitleUpdateContentTitleWriteFragmentArtifact from './__generated__/useContentTypesTitleUpdateContentTitleWriteFragment.graphql.ts';

type QueuedTitleUpdate = {
	title: string;
	resolve?: (success: boolean) => void;
};

type TitleUpdateData = {
	isEditing: boolean;
	setIsEditing: (boolean: boolean) => void;
	setUpdatedTitleFromPubsub: (value: string | undefined) => void;
	updateLoading: boolean;
	updateError?: ApolloErrorInterop;
	/** Fire-and-forget title save. Use `updateTitleAndWait` if you need the actual mutation outcome. */
	updateTitle: (value: string) => Promise<void>;
	updateTitleAndWait: (value: string) => Promise<boolean>;
	updateCache: (value: string) => void;
};

export const useContentTypesTitleUpdate = (
	contentId: string,
	currentTitle: string,
): TitleUpdateData => {
	const [isEditing, setIsEditing] = useState(false);
	const [updatedTitleFromPubsub, setUpdatedTitleFromPubsub] = useState<string | undefined>(
		undefined,
	);
	const isProcessingTitleUpdate = useRef(false);
	const onMutationComplete = () => {
		setUpdateError(undefined);
		setIsEditing(false);
	};
	const [updateError, setUpdateError] = useState<ApolloErrorInterop | undefined>(undefined);
	const [updateContentTitle, { loading: updateLoading }] = useMutationInterop<
		useContentTypesTitleUpdateContentTitleMutation$data,
		useContentTypesTitleUpdateContentTitleMutation$variables
	>(
		graphql`
			mutation useContentTypesTitleUpdateContentTitleMutation($contentId: ID!, $title: String) {
				updateTitle(contentId: $contentId, title: $title) {
					id
					title
					links {
						editui
						webui
					}
				}
			}
		`,
		{
			onCompleted: onMutationComplete,
			onError: setUpdateError,
		},
	);
	const writeFragmentInterop =
		useWriteFragmentInterop<useContentTypesTitleUpdateContentTitleWriteFragment$data>({
			fragmentArtifact: useContentTypesTitleUpdateContentTitleWriteFragmentArtifact,
			fragmentSpec: graphql`
				fragment useContentTypesTitleUpdateContentTitleWriteFragment on Content {
					title
					id
					__typename
				}
			`,
		});

	const updateCache = useCallback(
		(value: string) => {
			writeFragmentInterop({
				fragmentId: `Content:${contentId}`,
				data: {
					id: contentId,
					title: value,
					__typename: 'Content',
				},
			});
		},
		[contentId, writeFragmentInterop],
	);

	const updateTitle = useCallback(
		async (value: string): Promise<boolean> => {
			if (value === currentTitle) {
				setIsEditing(false);
				// We have to manually clear the error here because it is no longer relevant to this most recent call to `updateTitle`,
				// since we're now attempting to set the title back to the current title, which should be valid.
				setUpdateError(undefined);
				if (updatedTitleFromPubsub && updatedTitleFromPubsub !== currentTitle) {
					updateCache(updatedTitleFromPubsub);
					setUpdatedTitleFromPubsub(undefined);
				}
				if (!isProcessingTitleUpdate.current) {
					return true;
				}
			}

			try {
				const response = await updateContentTitle({
					variables: {
						contentId,
						title: value,
					},
				});
				return Boolean(response.data?.updateTitle);
			} catch {
				// error handled by useMutation result updateError
				return false;
			}
		},
		[contentId, currentTitle, updateCache, updateContentTitle, updatedTitleFromPubsub],
	);

	/**
	 * The following section is a mutation queue to ensure that title updates are processed sequentially and that the
	 * latest title is always the one that gets applied.
	 * This is important because multiple title updates can be triggered in quick succession when AI inline streaming
	 * occurs and we want to avoid stale state errors from the GQL store, but also ensure the latest title is the one that
	 * gets applied.
	 */
	const latestQueuedTitleUpdate = useRef<QueuedTitleUpdate | undefined>();

	const processQueue = useCallback(async (): Promise<void> => {
		if (isProcessingTitleUpdate.current) {
			return;
		}

		isProcessingTitleUpdate.current = true;

		try {
			let nextUpdate = latestQueuedTitleUpdate.current;
			latestQueuedTitleUpdate.current = undefined;

			// We keep updating the title until there are no more titles in the cache
			while (nextUpdate !== undefined) {
				const result = await updateTitle(nextUpdate.title);
				nextUpdate.resolve?.(result);
				nextUpdate = latestQueuedTitleUpdate.current;
				latestQueuedTitleUpdate.current = undefined;
			}
		} finally {
			isProcessingTitleUpdate.current = false;
		}
	}, [updateTitle]);

	const enqueueTitle = useCallback(
		async (title: string): Promise<void> => {
			latestQueuedTitleUpdate.current?.resolve?.(false);
			latestQueuedTitleUpdate.current = { title };

			if (!isProcessingTitleUpdate.current) {
				await processQueue();
			}
		},
		[processQueue],
	);

	// Fire-and-forget wrapper for the non-gated path. The underlying `updateTitle`
	// returns the mutation outcome but no public caller branches on it, so erase
	// the boolean here to keep the public type honest.
	const updateTitleVoid = useCallback(
		async (value: string): Promise<void> => {
			await updateTitle(value);
		},
		[updateTitle],
	);

	const enqueueTitleAndWait = useCallback(
		async (title: string): Promise<boolean> => {
			// Resolving the previous queued update as false means it was superseded, not that the BE mutation failed.
			latestQueuedTitleUpdate.current?.resolve?.(false);

			const result = new Promise<boolean>((resolve) => {
				latestQueuedTitleUpdate.current = { title, resolve };
			});

			if (!isProcessingTitleUpdate.current) {
				void processQueue();
			}

			return result;
		},
		[processQueue],
	);

	// Set the title change handler when the component mounts
	// Content types use this method to update the title from within the iframe
	useEffect(() => {
		contentTypesTitleServiceSingleton.setTitleChangeHandler(updateTitle);
		return () => {
			contentTypesTitleServiceSingleton.setTitleChangeHandler(() => {});
		};
	}, [updateTitle]);

	return {
		isEditing,
		setIsEditing,
		setUpdatedTitleFromPubsub,
		updateLoading,
		updateError,
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		updateTitle: fg('platform_editor_ai_aifc_patch_ga_blockers') ? enqueueTitle : updateTitleVoid,
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		updateTitleAndWait: fg('platform_editor_ai_aifc_patch_ga_blockers')
			? enqueueTitleAndWait
			: updateTitle,
		updateCache,
	};
};
